"use client";

import type { QueryClient } from "@tanstack/react-query";
import { QueryClientProvider } from "@tanstack/react-query";
import { createTRPCClient, httpBatchLink, TRPCLink } from "@trpc/client";
import { createTRPCContext } from "@trpc/tanstack-react-query";
import { observable } from "@trpc/server/observable";
import { useState } from "react";
import superjson from "superjson";
import { generateRequestKey, getOrCreatePendingRequest } from "@/lib/request-deduplication";
import { makeQueryClient } from "./query-client";
import type { AppRouter } from "./routers/_app";

export const { TRPCProvider, useTRPC } = createTRPCContext<AppRouter>();

/**
 * Custom tRPC link for request deduplication
 * Prevents duplicate requests from being sent to the server
 */
const deduplicationLink: TRPCLink<AppRouter> = () => {
  return ({ next, op }) => {
    return observable((observer) => {
      const key = generateRequestKey(op.path, op.input);
      
      let isOriginal = false;
      const promise = getOrCreatePendingRequest(key, () => {
        isOriginal = true;
        return new Promise((resolve, reject) => {
          next(op).subscribe({
            next(value) {
              resolve(value);
              observer.next(value);
            },
            error(err) {
              reject(err);
              observer.error(err);
            },
            complete() {
              observer.complete();
            },
          });
        });
      });

      // If it's a duplicate, we wait for the original promise and then notify this observer
      if (!isOriginal) {
        promise.then((value) => {
          observer.next(value);
          observer.complete();
        }).catch((err) => {
          observer.error(err);
        });
      }
    });
  };
};

let browserQueryClient: QueryClient;
function getQueryClient() {
  if (typeof window === "undefined") {
    // Server: always make a new query client
    return makeQueryClient();
  }
  // Browser: make a new query client if we don't already have one
  // This is very important, so we don't re-make a new client if React
  // suspends during the initial render. This may not be needed if we
  // have a suspense boundary BELOW the creation of the query client
  if (!browserQueryClient) browserQueryClient = makeQueryClient();
  return browserQueryClient;
}
function getUrl() {
  const base = (() => {
    if (typeof window !== "undefined") return "";
    if (process.env.VERCEL_URL) return `https://${process.env.VERCEL_URL}`;
    return "http://localhost:3000";
  })();
  return `${base}/api/trpc`;
}
export function TRPCReactProvider(
  props: Readonly<{
    children: React.ReactNode;
  }>,
) {
  const queryClient = getQueryClient();
  const [trpcClient] = useState(() =>
    createTRPCClient<AppRouter>({
      links: [
        deduplicationLink,
        httpBatchLink({
          transformer: superjson,
          url: getUrl(),
        }),
      ],
    }),
  );
  return (
    <QueryClientProvider client={queryClient}>
      <TRPCProvider trpcClient={trpcClient} queryClient={queryClient}>
        {props.children}
      </TRPCProvider>
    </QueryClientProvider>
  );
}
